<template>
  <div class='content'>
    <Grid v-if="!showCreateEnvelope"
          ref="grid"
          :name='$t("EvedPay Cards")'
          desc='Manage accounts, cards and transactions'
          search=true
          search-placeholder='My Transactions'
          resizing=false
          :endpoint="api.$.evedpaycard.transactions"
          :sort="sort"
          legacy
          footer
          :row-classes="rowClasses"
          :tabs='tabs'
          alias="EvedPayCards"
          @selectedRows="rowSelected"
          :no-tooltip-fields="noTooltipFields"
          :filter="{}"
          totals>
      <span slot="additionalButtons">
        <ed-button class="btn btn-actionbar"
                   :disabled="cardActionDisabled('createEnvelope')"
                   v-tooltip="actionDisabledReason('createEnvelope')"
                   @click="createEnvelope">
          CREATE ENVELOPE
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   :disabled="cardActionDisabled('reconcile')"
                   v-tooltip="actionDisabledReason('reconcile')"
                   @click="showModal(showReconcile)">
          RECONCILE
        </ed-button>
      </span>
      <span slot="additionalLabel" class="form-group no-margin">
        <i class="fas fa-exclamation"></i>
        <span>&nbsp;</span>
        <label>Click column headers to apply filters</label>
      </span>
    </Grid>
    <CreateEnvelope title="CREATE ENVELOPE"
                    :selected-rows="selectedRows"
                    :envelope="envelope"
                    v-if="showCreateEnvelope"
                    @success="showCreateEnvelope=!showCreateEnvelope; selectedRows = []; $router.push('/d/Programs/>Card/envelopes')"
                    @backToTransactions="showCreateEnvelope=!showCreateEnvelope; updateUrlBackToTransactionsPage(); selectedRows = []; envelope = {}"
                    @fields-updated="handleFieldsUpdated">
    </CreateEnvelope>
    <ed-modal :type="fields && fields.length > 0 && fields[0] && fields[0].length > 0 ? 'extra-large' : 'medium-large'"
              title="Reconcile Transaction"
              :show="showReconcile"
              @close="showModal(showReconcile); closeReconcile()"
              @validate="saveAndReconcile"
              :primary-button="(isUserAdmin || reconcileAction !== 'view') ? 'Save' : 'false'"
              :primaryButtonDisabled="!uploadFinished"
              validation>
      <Reconcile slot="body"
                 v-if="transaction"
                 :transaction="transaction"
                 :fields-incomplete="{ receiptIncomplete, descriptionIncomplete }"
                 :reconcile-action="reconcileAction"
                 @reconcile-created="handleReconcileCreated"
                 @fields-updated="handleFieldsUpdated"
                 @mediaFilesPathUpdated="updateMediaFilesPath"
                 @attachmentsUpdated="updateAttachmentsFiles"
                 @split-amounts="handleSplitAmounts"
                 @description-text="handleDescriptionText"
                 @uploadFinished="handleUploadFinished"
                 ref="reconcileComp">
      </Reconcile>
    </ed-modal>
  </div>
</template>

<script>

import Grid from '@/components/Grid'
import model from './model.js'
import tabs from '@/components/_modules/Programs/Card-transactions/tabs'
import CreateEnvelope from './CreateEnvelope.vue'
import Reconcile from './ReconcileTransaction.vue'

import { EventBus } from '@/event-bus'
import events from '@/core/events/events'

const cancelTooltip = 'Please select a transaction.'

// reload page after browser back pressed
function handlePopState(event) {
  if (document.location.href.includes('transactions')) {
    window.location.reload()
  }
}
window.addEventListener('popstate', handlePopState)

export default {
  components: {
    Grid,
    CreateEnvelope,
    Reconcile
  },
  data() {
    return {
      model,
      sort: { TransactionDateFormatted: 'desc' },
      tabs: tabs.tabs,
      selectedProgram: 0,
      selectedSupplier: 0,
      selectedRows: [],
      selectedAll: null,
      showCreateEnvelope: false,
      showReconcile: false,
      fields: [],
      formKey: '',
      splitAmounts: [],
      noteText: '',
      mediaFilesPath: '',
      filteredAttachments: [],
      requireReceiptDescription: Boolean,
      descriptionIncomplete: false,
      receiptIncomplete: false,
      savePressed: false,
      reconcileAction: 'create',
      selectedBu: [],
      selectedCc: [],
      selectedBuAndCcPair: [],
      selectedCategory: [],
      referenceId: [],
      splitCounter: 1,
      buAndCcPairIncomplete: [],
      buIncomplete: [],
      ccIncomplete: [],
      categoryIncomplete: [],
      itemIds: [],
      transaction: {},
      approvalUsersData: [],
      highlightedTransactionId: '',
      rowClasses: {
        'row-highlight': params => {
          return (params.data && (params.data.Id === this.highlightedTransactionId || params.data.EnvelopeId === this.highlightedTransactionId))
        }
      },
      envelope: {},
      noTooltipFields: ['TransactionStatus', 'ReconciliationStatus', 'TransactionNumber', 'EnvelopeNumber'],
      waitingApprovalStatuses: ['Open - Waiting Approval', 'Exported - Waiting Approval', 'Posted - Waiting Approval'],
      uploadFinished: true
    }
  },

  async created() {
    this.divisionName = this.singularize(this.$root.getUser.settings.Labels.DivisionName)
    this.divisionsName = this.$root.getUser.settings.Labels.DivisionsName
    this.model.Division.label = this.divisionName
    this.programName = this.singularize(this.$root.getUser.settings.Labels.ProgramLabel)
    this.model.ProgramName.label = this.programName
    EventBus.$off(events.EVEDPAY_CARDS.OPEN_RECONCILE)
    EventBus.$on(events.EVEDPAY_CARDS.OPEN_RECONCILE, this.handleTransaction)

    EventBus.$off(events.EVEDPAY_CARDS.OPEN_ENVELOPE)
    EventBus.$on(events.EVEDPAY_CARDS.OPEN_ENVELOPE, envelope => {
      this.envelope = envelope.data
      this.showCreateEnvelope = true
    })
    EventBus.$off('transaction-reconciled')
    EventBus.$on('transaction-reconciled', (transactionId) => {
      if (this.transaction && this.transaction.data) {
        const matchedTransaction = this.transaction.data.Id === transactionId.data

        if (matchedTransaction) {
          const transactionNumber = this.transaction.data.TransactionNumber
          this.$notifications.notify({
            message: `Transaction ${transactionNumber} was updated!`,
            type: 'info',
            placement: { from: 'top', align: 'center' }
          })
        }
      }
    })
  },

  computed: {
    selectedId() {
      return this.transaction && this.transaction.data &&
        this.transaction.data.PaymentInvoiceID
    },
    isUserAdmin() {
      if (this.$user.$.userPermissions.includes('EvedPayCardAdministrator')) {
        return true
      }
      return false
    }
  },

  activated() {
    this.selectedRows = []
    this.envelope = {}
  },

  watch: {
    showCreateEnvelope(newValue) {
      if (newValue) {
        this.highlightedTransactionId = this.envelope.Id
      }
    }
  },

  methods: {
    stopSave() {
      this.savePressed = false
    },

    async createReconcile() {
      let hasIncompleteField = false

      if (!(this.selectedProgram && Object.keys(this.selectedProgram).length !== 0 && this.selectedProgram.Id)) {
        hasIncompleteField = true
      }

      if ((!this.noteText || this.noteText && this.noteText.length === 0) && this.requireReceiptDescription && this.filteredAttachments.length !== 0) {
        this.descriptionIncomplete = true
        hasIncompleteField = true
      } else {
        this.descriptionIncomplete = false
      }

      if (this.filteredAttachments.length === 0) {
        hasIncompleteField = true
      }

      if (this.transaction.data.EnvelopeId &&
        this.transaction.data.EnvelopeId !== '00000000-0000-0000-0000-000000000000' &&
        this.filteredAttachments.length === 0 &&
        this.transaction.data.EnvelopeStatus !== 'Draft'
      ) {
        this.$notifications.notify({
          type: 'info-warn',
          message: 'Receipt is required. Please upload at least one file.',
          placement: { from: 'top', align: 'center' }
        })
        return this.stopSave()
      }

      this.buAndCcPairIncomplete = new Array(this.splitCounter).fill(false)
      this.buIncomplete = new Array(this.splitCounter).fill(false)
      this.ccIncomplete = new Array(this.splitCounter).fill(false)
      this.categoryIncomplete = new Array(this.splitCounter).fill(false)

      for (var i = 0; i < this.splitCounter; i++) {
        if (this.selectedBuAndCcPair) {
          this.buAndCcPairIncomplete[i] = !this.selectedBuAndCcPair[i]
        } else if (this.selectedBu && this.selectedCc) {
          this.buIncomplete[i] = !this.selectedBu[i]
          this.ccIncomplete[i] = !this.selectedCc[i]
        } else if (this.selectedBu && !this.selectedCc) {
          this.buIncomplete[i] = !this.selectedBu[i]
        } else if (!this.selectedBu && this.selectedCc) {
          this.ccIncomplete[i] = !this.selectedCc[i]
        }

        if (this.buAndCcPairIncomplete[i] || this.buIncomplete[i] || this.ccIncomplete[i]) {
          hasIncompleteField = true
        }

        if (!(this.selectedCategory && this.selectedCategory[i])) {
          hasIncompleteField = true
        }

        // check incomplete custom fields
        if (this.fields.some(item =>
          item.some(field => field && field.Required && (!field.Values || field.Values.length === 0))
        )) {
          hasIncompleteField = true
        }
      }

      if (this.descriptionIncomplete) {
        return this.stopSave()
      }

      try {
        const formCopy = JSON.parse(JSON.stringify(this.fields))
        const updatedFields = formCopy.map(i => {
          return i.map(x => {
            if (x.Type === 'select' && x.Values && typeof x.Values === 'object') {
              x.Values = x.Values.text
            } else if (x.Type === 'text' && x.Values && Array.isArray(x.Values) && x.Values.length > 0) {
              x.Values = x.Values[0]
            }
            return x
          })
        })

        let form = {
          Fields: updatedFields,
          CategoryIds: this.selectedCategory,
          ReferenceIds: this.referenceId,
          BusinessUnitIds: this.selectedBu,
          CostCenterIds: this.selectedCc,
          ProgramBusinessUnitAndCostCenterIds: this.selectedBuAndCcPair,
          Amounts: this.splitAmounts,
          TransactionReconcileSplitItemIds: this.itemIds
        }

        if (this.selectedCardId) {
          const response = await this.api.post(this.api.$.evedpaycard.reconcile, {
            preventError: true,
            ...this.newReconcile,
            CardId: this.selectedCardId,
            TransactionId: this.selectedTransactionId,
            SupplierId: this.selectedSupplier && this.selectedSupplier.Value ? this.selectedSupplier.Value.VendorId : null,
            ProgramId: this.selectedProgram && this.selectedProgram.Id ? this.selectedProgram.Id : null,
            ReceiptFiles: this.filteredAttachments,
            Note: this.noteText,
            SplitAmounts: this.splitAmounts,
            BusinessUnitIds: this.selectedBu,
            CostCenterIds: this.selectedCc,
            BusinessUnitAndCostCenterIds: this.selectedBuAndCcPair,
            CategoryIds: this.selectedCategory,
            ReferenceIds: this.referenceId,
            IsComplete: !hasIncompleteField,
            Form: form,
            FormKey: this.formKey
          })
          if (response.data) {
            return true
          } else {
            console.error('Error during the request. Response data is null.')
            this.$notifications.notify({ type: 'plain', icon: 'exclamation-circle', placement: { from: 'top', align: 'center' }, timeout: 0, message: 'An error occurred while saving. Please try again.', discard: 'Ok', goto: '' })
          }
        } else {
          console.error('Selected CardId is null or undefined.')
          this.$notifications.notify({ type: 'plain', icon: 'exclamation-circle', placement: { from: 'top', align: 'center' }, timeout: 0, message: 'An error occurred while saving. Please try again.', discard: 'Ok', goto: '' })
        }
      } catch (error) {
        console.error('Error during the request:', error)
        this.$notifications.notify({ type: 'plain', icon: 'exclamation-circle', placement: { from: 'top', align: 'center' }, timeout: 0, message: 'An error occurred while saving. Please try again.', discard: 'Ok', goto: '' })
      }
    },

    createEnvelope() {
      this.showCreateEnvelope = true
      window.history.pushState({}, '', '#/d/Programs/>Card/envelopes')
    },

    resetValidation() {
      this.receiptIncomplete = false
      this.descriptionIncomplete = false
      this.buAndCcPairIncomplete = new Array(this.splitCounter).fill(false)
      this.buIncomplete = new Array(this.splitCounter).fill(false)
      this.ccIncomplete = new Array(this.splitCounter).fill(false)
      this.categoryIncomplete = new Array(this.splitCounter).fill(false)
    },

    async saveAndReconcile() {
      if (!this.savePressed) {
        this.savePressed = true
        try {
          if (this.reconcileAction === 'view' && this.isUserAdmin) {
            if (this.filteredAttachments.length === 0) {
              this.$notifications.notify({
                type: 'info-warn',
                message: 'Receipt is required. Please upload at least one file.',
                placement: { from: 'top', align: 'center' }
              })
              return this.stopSave()
            }

            const payload = {
              transactionId: this.selectedTransactionId,
              receiptFiles: this.filteredAttachments.map(f => ({
                id: f.Id || '00000000-0000-0000-0000-000000000000',
                pathWithName: f.PathWithName,
                originalName: f.OriginalName,
                mediaFile: f.MediaFile,
                sizeBytes: f.SizeBytes,
                vendorId: f.VendorId
              }))
            }

            try {
              await this.api.post(
                this.api.$.evedpaycard.updateReceipt,
                payload
              )
              this.$notifications.notify({
                type: 'info',
                message: 'Receipt updated successfully',
                placement: { from: 'top', align: 'center' }
              })
              this.savePressed = false
              this.closeReconcile()
              this.resetModalFields()
            } catch (e) {
              console.error('Error while updating the receipt:', e)
            }
          } else {
            const r = await this.createReconcile()
            if (r) {
              EventBus.$emit('transaction-reconciled', this.transaction.data.Id)
              this.savePressed = false
              this.closeReconcile()
              this.resetModalFields()
            }
          }
        } finally {
          this.savePressed = false
        }
      }
    },

    updateMediaFilesPath(mediaFilesPath) {
      this.mediaFilesPath = mediaFilesPath
      this.receiptIncomplete = false
    },

    updateAttachmentsFiles(filteredAttachments) {
      this.filteredAttachments = filteredAttachments
    },

    handleFieldsUpdated(form) {
      this.fields = form.fields
      this.formKey = form.formKey
    },

    handleSplitAmounts(data) {
      this.splitAmounts = data
    },

    handleDescriptionText(data) {
      this.noteText = data.noteText
      this.requireReceiptDescription = data.requireReceiptDescription
    },

    handleReconcileCreated(data) {
      this.fields = data.fields ? data.fields : this.fields
      this.itemIds = data.itemIds
      this.splitCounter = data.splitCounter
      this.selectedSupplier = data.selectedSupplier
      this.selectedProgram = data.selectedProgram
      this.selectedBu = data.selectedBu
      this.selectedCc = data.selectedCc
      this.selectedBuAndCcPair = data.selectedBuAndCcPair
      this.selectedCategory = data.selectedCategory
      this.referenceId = data.referenceId
    },

    handleUploadFinished(finished) {
      this.uploadFinished = finished
    },

    singularize(value) {
      return value.endsWith('s') ? value.slice(0, -1) : value
    },

    cardActionDisabled(actionName) {
      return !!this.actionDisabledReason(actionName)
    },

    actionDisabledReason(actionName) {
      if (this.selectedRows.length > 1 && actionName === 'reconcile') {
        return 'You can only reconcile one transaction at a time.'
      }

      if (this.selectedRows.length > 0) {
        const firstCardId = this.selectedRows[0].data.CardId
        const programIds = Array.from(new Set(this.selectedRows.map(row => row && row.data.ProgramId !== null ? row.data.ProgramId : null).filter(id => id !== null)))

        if (programIds.length > 1 && actionName === 'createEnvelope') {
          return `Only transactions from the same ${this.programName.toLowerCase()} that are not already part of an envelope can be selected.`
        }

        for (var row of this.selectedRows) {
          if (row.data.TransactionStatus === 'Rejected' || row.data.TransactionStatus === 'Declined') {
            return 'Only transactions that are not Rejected or Declined can be selected.'
          }

          if (row.data.TransactionStatus === 'Pending' && actionName !== 'reconcile') {
            return 'Only transactions that are not Pending or Declined can be selected.'
          }

          if (row.data.EnvelopeNumber !== null && actionName === 'createEnvelope') {
            return 'Only transactions that are not already part of an envelope can be selected.'
          }

          if (row.data.CardId !== firstCardId) {
            return 'Only transactions from the same card that are not already part of an envelope can be selected.'
          }
        }
      }

      if (this.selectedRows.length === 0) {
        return cancelTooltip
      }
    },

    async rowSelected(event) {
      if (typeof event === 'boolean') {
        this.selectedAll = event
      } else {
        this.selectedRows = event
        this.transaction = this.selectedRows[0]
        if (this.highlightedTransactionId && this.highlightedTransactionId.length > 0 && !this.showReconcile && this.selectedRows.length > 0) {
          this.highlightedTransactionId = ''
          this.redrawRows()
        }
        if (this.transaction && this.transaction.data) {
          this.selectedCardId = this.transaction.data.CardId
          this.selectedTransactionId = this.transaction.data.Id
          if (this.waitingApprovalStatuses.includes(this.transaction.data.EnvelopeStatus)) {
            await this.getApprovalUsers(this.transaction.data.EnvelopeId)
          }
          await this.handleReconcileAction(this.transaction)
        } else {
          this.reconcileAction = 'create'
        }
      }
    },

    createReconciliationEnvelope() {
      if (this.selectedRows.length === 0) {
        console.error('No data')
        return
      }
    },

    showModal(showReconcile) {
      if (!this.savePressed) {
        this.showReconcile = !showReconcile
        if (this.showReconcile) {
          this.highlightedTransactionId = this.transaction.data.Id
        } else {
          this.redrawRows()
        }
        this.resetModalFields()
      }
    },

    resetFields() {
      this.savePressed = false
      this.selectedProgram = ''
      this.selectedSupplier = ''
      this.noteText = ''
      this.mediaFilesPath = ''
      this.filteredAttachments = []
      this.fields = []
      this.formKey = ''
    },

    resetModalFields() {
      if (this.showReconcile) {
        if (this.$refs.reconcileComp) {
          this.$refs.reconcileComp.$refs.formRef.reset()
          this.resetValidation()
          this.resetFields()
        }
      } else {
        this.$refs.grid.onSelectAll(false)
        this.selectedRows = []
        this.transaction = {}
        this.approvalUsersData = []
        this.$refs.reconcileComp.$refs.formRef.reset()
        this.resetValidation()
        this.resetFields()
      }
    },

    closeReconcile() {
      if (!this.savePressed) {
        this.showReconcile = false
        this.selectedRows = []
        this.transaction = {}
        this.approvalUsersData = []
        this.refresh()
        this.resetValidation()
        this.resetFields()
      }
    },

    async handleTransaction(transaction) {
      this.transaction = transaction
      if (this.transaction && this.transaction.data) {
        this.selectedCardId = this.transaction.data.CardId
        this.selectedTransactionId = this.transaction.data.Id
        if (this.waitingApprovalStatuses.includes(this.transaction.data.EnvelopeStatus)) {
          await this.getApprovalUsers(this.transaction.data.EnvelopeId)
        }
        await this.handleReconcileAction(this.transaction)
      } else {
        this.reconcileAction = 'create'
      }
      this.showReconcile = true
      this.highlightedTransactionId = this.transaction.data.Id
    },

    async handleReconcileAction(transaction) {
      const readonlyStatuses = ['In Envelope', 'Exported', 'Posted']
      if (transaction.data.ReconciliationStatus !== 'Open') {
        if (transaction.data.ReconciliationStatus === 'Coded') {
          if (transaction.data.EnvelopeStatus === 'Draft') {
            this.reconcileAction = 'edit'
          } else if (!readonlyStatuses.includes(transaction.data.TransactionStatus) || this.isUserInApprovalList()) {
            this.reconcileAction = 'edit'
          } else {
            this.reconcileAction = 'view'
          }
        } else {
          this.reconcileAction = 'view'
        }
      } else if (readonlyStatuses.includes(transaction.data.TransactionStatus)) {
        if (transaction.data.EnvelopeStatus === 'Draft') {
          this.reconcileAction = 'edit'
        } else if (this.isUserInApprovalList()) {
          this.reconcileAction = 'edit'
        } else {
          this.reconcileAction = 'view'
        }
      } else {
        this.reconcileAction = 'create'
      }
    },

    async getApprovalUsers(envelopeId) {
      await this.api.get(this.api.$.evedpaycard.approvalUsersForEnvelope, {
        params: {
          envelopeId
        }
      })
        .then(response => {
          this.approvalUsersData = response.data
        })
        .catch(error => {
          console.error('There was a problem with the approval users request:', error)
        })
    },

    isUserInApprovalList() {
      const currentUserID = this.$root.getUser.$.user.UserID
      if (Array.isArray(this.approvalUsersData)) {
        return this.approvalUsersData.some(user => user.Id === currentUserID && user.IsCurrentAction)
      }
      return false
    },

    updateUrlBackToTransactionsPage() {
      window.history.pushState({}, '', '#/d/Programs/>Card/transactions')
    },

    refresh() {
      this.$refs.grid.refreshGrid()
    },

    redrawRows() {
      this.$refs.grid.gridOptions.api.redrawRows()
    }
  }
}

</script>

<style lang="scss" scoped>
  @import "~@/assets/sass/_variables.scss";

  .grid >>> {
    .ag-row.row-highlight {
      @include theme {
        background-color: #DBE9F9;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-transactions/index.vue